// src/hooks/useSmartDocument.js
import { useState, useCallback } from "react";
import { useAuth } from "./useAuth";

export default function useSmartDocument() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 세션 목록 조회
  const listSessions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get("/smart_editor/session_list_20250826");
      if (!response.data.status) {
        throw new Error(
          response.data.error || "세션 목록을 불러올 수 없습니다"
        );
      }
      return {
        ok: true,
        data: response.data.data || [],
      };
    } catch (err) {
      setError(err.message);
      return {
        ok: false,
        error: err.message,
        data: [],
      };
    } finally {
      setLoading(false);
    }
  }, [api]);

  // 세션 상세 조회
  const getSession = useCallback(
    async (sessionId) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.get(
          `/smart_editor/session_get_20250826?session_id=${sessionId}`
        );
        if (!response.data.status) {
          throw new Error("세션을 불러올 수 없습니다");
        }
        return {
          ok: true,
          session: response.data.session,
          databases: response.data.session.databases || [],
        };
      } catch (err) {
        setError(err.message);
        return {
          ok: false,
          error: err.message,
          databases: [],
        };
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  // 버전 목록 조회
  const listVersions = useCallback(
    async (sessionId) => {
      try {
        const response = await api.get(
          `/smart_editor/session_version_list_20250826?session_id=${sessionId}`
        );
        if (!response.data.status) {
          throw new Error("버전 목록을 불러올 수 없습니다");
        }
        return {
          ok: true,
          items: response.data.items || [],
          total: response.data.total || 0,
        };
      } catch (err) {
        return {
          ok: false,
          error: err.message,
          items: [],
          total: 0,
        };
      }
    },
    [api]
  );

  // 특정 버전 조회
  const getVersion = useCallback(
    async (sessionId, versionNumber = 0) => {
      setLoading(true);
      try {
        const response = await api.get(
          `/smart_editor/session_version_get_20250826?session_id=${sessionId}&version=${versionNumber}`
        );
        if (!response.data.status) {
          throw new Error("버전을 불러올 수 없습니다");
        }
        return {
          ok: true,
          version: response.data.version,
          sections: response.data.sections || [],
        };
      } catch (err) {
        return {
          ok: false,
          error: err.message,
        };
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  // 편집 이력 조회
  const listEditHistory = useCallback(
    async (sessionId) => {
      try {
        const response = await api.get(
          `/smart_editor/session_edit_list_20250826?session_id=${sessionId}`
        );
        if (!response.data.status) {
          throw new Error("편집 이력을 불러올 수 없습니다");
        }
        return {
          ok: true,
          items: response.data.items || [],
          total: response.data.total || 0,
        };
      } catch (err) {
        return {
          ok: false,
          error: err.message,
          items: [],
          total: 0,
        };
      }
    },
    [api]
  );

  // 편집 상세 조회
  const getEditDetail = useCallback(
    async (editId) => {
      try {
        const response = await api.get(
          `/smart_editor/session_edit_detail_20250826?edit_id=${editId}`
        );
        if (!response.data.status) {
          throw new Error("편집 상세를 불러올 수 없습니다");
        }
        return {
          ok: true,
          history: response.data.history,
          modifications: response.data.section_modifications || [],
        };
      } catch (err) {
        return {
          ok: false,
          error: err.message,
        };
      }
    },
    [api]
  );

  // 채팅 메시지 조회
  const listChatMessages = useCallback(
    async (sessionId) => {
      try {
        const response = await api.get(
          `/smart_editor/session_chat_list?session_id=${sessionId}`
        );
        if (!response.data.status) {
          throw new Error("채팅 메시지를 불러올 수 없습니다");
        }
        return {
          ok: true,
          messages: response.data.messages || [],
          count: response.data.count || 0,
        };
      } catch (err) {
        return {
          ok: false,
          error: err.message,
          messages: [],
          count: 0,
        };
      }
    },
    [api]
  );

  // 문서 생성
  const createDocument = useCallback(
    async (data) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.post(
          "/smart_editor/create_document_20250826",
          {
            template_base64: data.templateBase64,
            instructions: data.instructions,
            section_hints: data.sectionHints || {},
            additional_data: data.additionalData || {},
            target_format: data.targetFormat || "html",
            document_ids: data.documentIds,
            scope: data.scope || "enterprise",
            template_name: data.template_name,
          }
        );

        if (!response.data.status) {
          throw new Error(response.data.error || "문서 생성에 실패했습니다");
        }

        return {
          ok: true,
          sessionId: response.data.session_id,
          version: response.data.version,
          confidence: response.data.confidence,
          totalChunksUsed: response.data.total_chunks_used,
          filledHTML: response.data.filled_html,
          sectionResults: response.data.section_results,
        };
      } catch (err) {
        setError(err.message);
        return {
          ok: false,
          error: err.message,
        };
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  // 문서 편집
  const editDocument = useCallback(
    async (sessionId, userRequest) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.post(
          "/smart_editor/edit_document_20250826",
          {
            session_id: sessionId,
            user_request: userRequest,
          }
        );

        if (!response.data.status) {
          throw new Error(response.data.error || "문서 편집에 실패했습니다");
        }

        return {
          ok: true,
          newVersion: response.data.new_version,
          analysis: response.data.analysis,
          modifiedSections: response.data.modified_sections,
          fullDocument: response.data.full_document,
          processingTime: response.data.processing_time,
        };
      } catch (err) {
        setError(err.message);
        return {
          ok: false,
          error: err.message,
        };
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  // 문서 삭제
  const deleteDocument = useCallback(
    async (sessionId) => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.post(
          "/smart_editor/session_delete_20250826",
          {
            session_id: sessionId,
          }
        );

        if (!response.data.status) {
          throw new Error(response.data.error || "문서 삭제에 실패했습니다");
        }

        return {
          ok: true,
          deleted: response.data.deleted,
        };
      } catch (err) {
        setError(err.message);
        return {
          ok: false,
          error: err.message,
        };
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  // 파일을 Base64로 변환
  const fileToBase64 = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(",")[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }, []);

  // PDF 파일 검증
  const validatePDFFile = useCallback((file) => {
    if (!file) {
      return { valid: false, error: "파일을 선택해주세요." };
    }
    if (file.type !== "application/pdf") {
      return { valid: false, error: "PDF 파일만 업로드 가능합니다." };
    }
    const maxSize = 15 * 1024 * 1024; // 15MB
    if (file.size > maxSize) {
      return { valid: false, error: "파일 크기는 15MB 이하여야 합니다." };
    }
    return { valid: true };
  }, []);

  // 날짜 포맷팅
  const formatDate = useCallback((dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      const hours = Math.floor(diff / (1000 * 60 * 60));
      if (hours === 0) {
        const minutes = Math.floor(diff / (1000 * 60));
        return minutes === 0 ? "방금 전" : `${minutes}분 전`;
      }
      return `${hours}시간 전`;
    }
    if (days === 1) return "어제";
    if (days < 7) return `${days}일 전`;

    return date.toLocaleDateString("ko-KR");
  }, []);

  return {
    loading,
    error,
    listSessions,
    getSession,
    listVersions,
    getVersion,
    listEditHistory,
    getEditDetail,
    listChatMessages,
    createDocument,
    editDocument,
    deleteDocument,
    fileToBase64,
    validatePDFFile,
    formatDate,
  };
}
